import type {RepositoryNWO} from '@github-ui/current-repository'
import {SignedCommitBadge as UiSignedCommitBadge} from '@github-ui/signed-commit-badge'
import {LoadingSkeleton} from '@github-ui/skeleton/LoadingSkeleton'
import {VerifiedIcon} from '@primer/octicons-react'
import {Label, Link} from '@primer/react'
import {Dialog, type DialogHeaderProps} from '@primer/react/experimental'
import {format} from 'date-fns'
import {type KeyboardEvent, useDeferredValue, useRef, useState} from 'react'

import {useIsDeferredDataLoading} from '../contexts/DeferredCommitDataContext'
import {useCommitsLayoutPayload} from '../hooks/use-commits-layout-payload'
import type {DeferredCommitData} from '../types/commits-types'
import {AsyncChecksStatusBadge} from './AsyncChecksStatusBadge'
import styles from './CommitRowBadges.module.css'

export function AuthenticatedCommitBadge({deferredData}: {deferredData: DeferredCommitData | undefined}) {
  const [isOpen, setIsOpen] = useState(false)
  const badgeRef = useRef<HTMLButtonElement>(null)
  let latestProof = deferredData?.proofOfPresence?.[0]
  for (const proof of deferredData?.proofOfPresence ?? []) {
    if (latestProof && Date.parse(proof.recordedAt) > Date.parse(latestProof.recordedAt)) {
      latestProof = proof
    }
  }

  const deferredLatestProof = useDeferredValue(latestProof)
  const proofToRender = latestProof ?? deferredLatestProof
  const isDialogOpen = isOpen && latestProof !== undefined
  if (!proofToRender) return null
  const {actor, recordedAt} = proofToRender

  function onDialogKeyDown(event: KeyboardEvent) {
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- Preserve Primer's Escape handling before parent list shortcuts.
    if (event.key === 'Escape') {
      event.stopPropagation()
      setIsOpen(false)
    }
  }

  return (
    <>
      <Label
        as="button"
        type="button"
        size="small"
        variant="success"
        className="cursor-pointer"
        aria-haspopup="dialog"
        aria-expanded={isDialogOpen}
        ref={badgeRef}
        onClick={() => setIsOpen(true)}
      >
        Authenticated
      </Label>
      {isDialogOpen && (
        <Dialog
          title="Proof of presence"
          width="small"
          onClose={() => setIsOpen(false)}
          returnFocusRef={badgeRef}
          className="overflow-y-auto bgColor-default"
          renderHeader={({dialogLabelId, dialogDescriptionId}: DialogHeaderProps) => (
            <div className={styles.dialogHeader}>
              <Dialog.Title id={dialogLabelId} className="sr-only">
                Proof of presence
              </Dialog.Title>
              <div id={dialogDescriptionId} className="d-flex p-2 flex-1">
                <span className="pr-2 color-fg-success">
                  <VerifiedIcon />
                </span>
                <div className={styles.message}>
                  <strong>
                    {actor ? (
                      <Link href={`/${actor.login}`} inline className={styles.actor} onKeyDown={onDialogKeyDown}>
                        {actor.login}
                      </Link>
                    ) : (
                      'An unavailable user'
                    )}{' '}
                    authenticated this commit with <span className="color-fg-success">proof of presence</span>
                  </strong>
                </div>
              </div>
              <Dialog.CloseButton onClose={() => setIsOpen(false)} onKeyDown={onDialogKeyDown} />
            </div>
          )}
          renderBody={() => (
            <Dialog.Body className={`tmp-p-3 ${styles.record}`}>
              <div>
                Completed on{' '}
                <time dateTime={recordedAt} data-testid="proof-recorded-at">
                  {format(new Date(recordedAt), 'MMM d, yyyy, h:mm a')}
                </time>
              </div>
              <Link
                href="https://docs.github.com/en/enterprise-cloud@latest/admin/configuring-settings/hardening-security-for-your-enterprise/configuring-proof-of-presence"
                className="Link--inTextBlock"
                onKeyDown={onDialogKeyDown}
              >
                Learn more about proof of presence
              </Link>
            </Dialog.Body>
          )}
        />
      )}
    </>
  )
}

export function SignedCommitBadge({deferredData}: {deferredData: DeferredCommitData | undefined}) {
  const {helpUrl} = useCommitsLayoutPayload()
  const isLoading = useIsDeferredDataLoading()

  return (
    <>
      {isLoading && <LoadingSkeleton variant="rounded" className="d-none d-sm-flex" width="62px" />}
      {!isLoading && deferredData === undefined && (
        <LoadingSkeleton variant="rounded" className="d-none d-sm-flex" width="62px" />
      )}
      {!isLoading && deferredData && (
        <UiSignedCommitBadge
          commitOid={deferredData.oid}
          hasSignature
          helpUrl={helpUrl}
          verificationStatus={deferredData.verifiedStatus}
          signature={deferredData.signatureInformation ? {helpUrl, ...deferredData.signatureInformation} : undefined}
        />
      )}
    </>
  )
}

export function CommitChecksStatusBadge({
  deferredData,
  oid,
  repository,
}: {
  deferredData: DeferredCommitData | undefined
  oid: string
  repository: RepositoryNWO
}) {
  const isLoading = useIsDeferredDataLoading()

  let checkStatusCount = ''
  try {
    checkStatusCount = deferredData?.statusCheckStatus?.short_text?.split('checks')[0]?.trim() || ''
  } catch {
    //noop
  }

  return (
    <>
      {isLoading && <LoadingSkeleton variant="rounded" className="d-none d-sm-flex ml-2" width="20px" />}
      {!isLoading && deferredData === undefined && (
        <LoadingSkeleton variant="rounded" className="d-none d-sm-flex ml-2" width="20px" />
      )}
      {!isLoading && deferredData?.statusCheckStatus && <div className="d-none d-sm-flex ml-1">&middot;</div>}
      <div className="d-none d-sm-flex">
        <AsyncChecksStatusBadge
          oid={oid}
          status={deferredData?.statusCheckStatus?.state}
          descriptionString={checkStatusCount}
          repo={repository}
        />
      </div>
    </>
  )
}
